import { v4 as uuid } from 'uuid';

import { requestProjectModelLabelsAndDescriptions } from 'src/actions/projects/action-creators/requestProjectModelLabelsAndDescriptions';
import {
  projectsActionCreators,
  projectsStardogRequestDispatchers,
} from 'src/actions/projects/actionCreators';
import { projectModelSelector } from 'src/selectors/projectModel';
import { ProjectModel } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';

export const addModelsToProject =
  ({
    projectId,
    connectionId,
    databaseId,
    modelNames,
  }: {
    projectId: string;
    connectionId: string;
    databaseId: string;
    modelNames: string[];
  }) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const { databases } = getState();
    const { namedGraphsByModel = {}, namespaces = {} } =
      databases.databasesById?.[databaseId] || {};

    const modelNamesAndIds = modelNames.map((modelName) => ({
      modelName,
      modelId: uuid(),
    }));
    dispatch(
      projectsActionCreators.addModelsToProjectStarted({
        id: projectId,
        connectionId,
        databaseId,
        databaseNamespaces: namespaces,
        modelNamesAndIds,
      })
    );

    await Promise.all(
      modelNamesAndIds.map(async ({ modelName, modelId }) => {
        const namedGraphs = namedGraphsByModel[modelName];
        if (namedGraphs) {
          await Promise.all([
            projectsStardogRequestDispatchers.requestProjectModelClasses({
              id: projectId,
              modelId,
              databaseId,
              namedGraphs,
            }),
            projectsStardogRequestDispatchers.requestProjectModelProperties({
              id: projectId,
              modelId,
              databaseId,
              namedGraphs,
            }),
            projectsStardogRequestDispatchers.requestProjectModelRules({
              id: projectId,
              modelId,
              databaseId,
              namedGraphs,
            }),
          ]);

          const { classes, properties } = projectModelSelector(
            projectId,
            modelId
          )(getState()) as ProjectModel;

          const allIris = new Set<string>();
          Object.values(classes || {}).forEach(({ iri, parents }) => {
            allIris.add(iri);
            parents.forEach((parent) => allIris.add(parent));
          });
          Object.values(properties || {}).forEach(
            ({ iri, domain: domains, range: ranges, parents }) => {
              allIris.add(iri);
              domains.forEach((domain) => allIris.add(domain));
              ranges.forEach((range) => allIris.add(range));
              parents.forEach((parent) => allIris.add(parent));
            }
          );

          await dispatch(
            requestProjectModelLabelsAndDescriptions({
              projectId,
              modelId,
              databaseId,
              namedGraphs,
              allIris,
            })
          );
        }
      })
    );

    dispatch(
      projectsActionCreators.addModelsToProjectFinished(
        projectId,
        modelNamesAndIds
      )
    );
  };
